// One classifier for sign-up. Kinds come from error codes, never from message text.
// Sign-in does not use this module.

import { ApiError } from "../api/unwrap";

export type SignupFailure =
  | { kind: "email-in-use" }
  | { kind: "weak-password"; policy?: string }
  | { kind: "invalid-email" }
  | { kind: "too-many" }
  | { kind: "network" }
  | { kind: "join-code" }
  | { kind: "different-method" }
  | { kind: "other"; message: string };

const GENERIC = "Could not create the account. Try again.";

// createUserWithEmailAndPassword is the only caller. Enumeration protection
// can return either code when the email already exists. Sign-in does not use this map.
const FIREBASE_KIND: Record<string, Exclude<SignupFailure["kind"], "other">> = {
  "auth/email-already-in-use": "email-in-use",
  "auth/invalid-credential": "email-in-use",
  "auth/weak-password": "weak-password",
  "auth/password-does-not-meet-requirements": "weak-password",
  "auth/invalid-email": "invalid-email",
  "auth/missing-email": "invalid-email",
  "auth/too-many-requests": "too-many",
  "auth/network-request-failed": "network",
  "auth/timeout": "network",
  "auth/missing-password": "weak-password",
  "auth/account-exists-with-different-credential": "different-method",
};

export class SignupError extends Error {
  readonly failure: SignupFailure;
  constructor(failure: SignupFailure, options?: { cause?: unknown }) {
    super(signupFailureText(failure), options);
    this.name = "SignupError";
    this.failure = failure;
  }
}

export function classifySignupError(e: unknown): SignupFailure {
  if (e instanceof SignupError) return e.failure;
  if (e instanceof ApiError) return classifyApiSignup(e);
  const firebase = classifyFirebaseSignup(e);
  if (firebase) return firebase;
  const transport = classifyTransport(e);
  if (transport) return transport;
  return { kind: "other", message: unknownMessage(e) };
}

export function signupFailureText(failure: SignupFailure): string {
  switch (failure.kind) {
    case "email-in-use":
      return "That email is already in use.";
    case "weak-password":
      return failure.policy ?? "Use a longer password (at least 6 characters).";
    case "invalid-email":
      return "Enter a valid email address.";
    case "too-many":
      return "Too many attempts. Wait a few minutes and try again.";
    case "network":
      return "Can't reach the server. Check your connection and try again.";
    case "join-code":
      return "That join code isn’t valid — nothing was created. Check it with whoever invited you.";
    case "different-method":
      return "That email uses a different sign-in method.";
    case "other":
      return GENERIC;
  }
}

function classifyFirebaseSignup(e: unknown): SignupFailure | null {
  const code = firebaseCode(e);
  if (!code) return null;
  const kind = FIREBASE_KIND[code];
  if (!kind) return { kind: "other", message: unknownMessage(e) };
  if (code !== "auth/password-does-not-meet-requirements") return { kind };
  const policy = firebasePolicy(e);
  return policy ? { kind: "weak-password", policy } : { kind: "weak-password" };
}

function firebaseCode(e: unknown): string | null {
  if (!e || typeof e !== "object") return null;
  const code = (e as { code?: unknown }).code;
  return typeof code === "string" && code.startsWith("auth/") ? code : null;
}

function firebasePolicy(e: unknown): string | undefined {
  if (!e || typeof e !== "object") return undefined;
  const message = (e as { message?: unknown }).message;
  if (typeof message !== "string") return undefined;
  const text = message
    .replace(/^Firebase:\s*/i, "")
    .replace(/\s*\(auth\/password-does-not-meet-requirements\)\.?\s*$/i, "")
    .trim();
  if (!text || text.startsWith("auth/")) return undefined;
  return text;
}

function classifyApiSignup(e: ApiError): SignupFailure {
  if (e.code === "ERR_EMAIL_TAKEN") return { kind: "email-in-use" };
  if (e.code === "ERR_RATE_LIMITED" || e.status === 429)
    return { kind: "too-many" };
  if (e.code === "ERR_VALIDATION" && mentionsField(e, "join_code"))
    return { kind: "join-code" };
  if (e.code === "ERR_VALIDATION" && mentionsField(e, "email"))
    return { kind: "invalid-email" };
  if (isServerDown(e)) return { kind: "network" };
  return { kind: "other", message: e.message || GENERIC };
}

function mentionsField(e: ApiError, name: string): boolean {
  if (e.section === name) return true;
  return (e.details ?? []).some((row) => {
    const field = row.field ?? "";
    return field === name || field.endsWith(`.${name}`);
  });
}

function isServerDown(e: ApiError): boolean {
  if (e.status != null && e.status >= 500) return true;
  return (
    e.code === "ERR_INTERNAL" ||
    e.code === "ERR_DEPENDENCY_UNAVAILABLE" ||
    e.code === "ERR_TIMEOUT"
  );
}

function classifyTransport(e: unknown): SignupFailure | null {
  if (e instanceof TypeError) return { kind: "network" };
  if (!isAxiosError(e)) return null;
  const status = axiosStatus(e);
  if (status === 429) return { kind: "too-many" };
  if (status == null || status === 0 || status >= 500)
    return { kind: "network" };
  return null;
}

function isAxiosError(e: unknown): boolean {
  return (
    !!e &&
    typeof e === "object" &&
    (e as { isAxiosError?: boolean }).isAxiosError === true
  );
}

function axiosStatus(e: unknown): number | undefined {
  const status = (e as { response?: { status?: unknown } }).response?.status;
  return typeof status === "number" ? status : undefined;
}

function unknownMessage(e: unknown): string {
  if (e instanceof Error && e.message.trim()) return e.message;
  if (e && typeof e === "object") {
    const message = (e as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message.trim();
  }
  return GENERIC;
}

// Password reset is not sign-up, so it has its own small map. An unknown reset
// failure never says "Could not create the account", and auth/invalid-credential
// never says "already in use" here (that mapping is for the create call only).
const RESET_PROBLEM: Record<string, string> = {
  "auth/invalid-email": "Enter a valid email address.",
  "auth/missing-email": "Enter a valid email address.",
  "auth/too-many-requests":
    "Too many attempts. Wait a few minutes and try again.",
  "auth/network-request-failed":
    "Can't reach the server. Check your connection and try again.",
};

export const RESET_FAILED = "Could not send a reset email. Try again.";

/**
 * The problem to show for a failed password reset, or null when the screen should
 * show the neutral "on its way" line. auth/user-not-found stays neutral so the reset
 * copy never says whether an account exists.
 */
export function passwordResetProblem(e: unknown): string | null {
  const code = firebaseCode(e);
  if (code === "auth/user-not-found") return null;
  return (code && RESET_PROBLEM[code]) || RESET_FAILED;
}
